/**
 * Lucide React 图标文档
 * 版本: v0.294.0
 * 
 * 使用方法:
 * 1. 静态导入 (推荐，打包时包含所有图标):
 *    import { Search, Plus, Edit } from 'lucide-react';
 * 
 * 2. 动态导入 (按需加载，适合代码分割):
 *    import { dynamicIconImports } from 'lucide-react';
 *    const Search = dynamicIconImports['search'];
 * 
 * 3. 从本文件批量导入常用图标:
 *    import { CommonIcons, NavigationIcons, ActionIcons } from '@/components/lucideIcons';
 */

// ============ 常用图标快捷引用 ============
// 这些是最常用的图标，按分类整理

export const CommonIconsList = {
  // 导航类
  menu: 'menu',
  x: 'x',
  chevronLeft: 'chevron-left',
  chevronRight: 'chevron-right',
  chevronDown: 'chevron-down',
  chevronUp: 'chevron-up',
  arrowLeft: 'arrow-left',
  arrowRight: 'arrow-right',
  arrowUp: 'arrow-up',
  arrowDown: 'arrow-down',
  home: 'home',
  layout: 'layout',
  grid: 'grid',
  list: 'list',
  moreHorizontal: 'more-horizontal',
  moreVertical: 'more-vertical',

  // 操作类
  plus: 'plus',
  minus: 'minus',
  check: 'check',
  checkCircle: 'check-circle',
  circle: 'circle',
  trash: 'trash',
  trash2: 'trash-2',
  edit: 'edit',
  pencil: 'pencil',
  copy: 'copy',
  save: 'save',
  download: 'download',
  upload: 'upload',
  search: 'search',
  filter: 'filter',
  refresh: 'refresh',
  undo: 'undo',
  redo: 'redo',
  send: 'send',
  share: 'share',
  bookmark: 'bookmark',
  flag: 'flag',
  printer: 'printer',
  link: 'link',
  externalLink: 'external-link',
  crop: 'crop',
  move: 'move',

  // 安全/权限类
  lock: 'lock',
  unlock: 'unlock',
  shield: 'shield',
  shieldCheck: 'shield-check',
  key: 'key',
  eye: 'eye',
  eyeOff: 'eye-off',

  // 设置类
  settings: 'settings',
  tool: 'tool',
  wrench: 'wrench',
  sliders: 'sliders',
  toggleLeft: 'toggle-left',
  toggleRight: 'toggle-right',
  power: 'power',
  logIn: 'log-in',
  logOut: 'log-out',

  // 用户/社交类
  user: 'user',
  userPlus: 'user-plus',
  userMinus: 'user-minus',
  userCheck: 'user-check',
  userX: 'user-x',
  users: 'users',
  group: 'group',
  idCard: 'id-card',

  // 企业/业务类
  building: 'building',
  briefcase: 'briefcase',
  store: 'store',
  cart: 'cart',
  shoppingCart: 'shopping-cart',
  creditCard: 'credit-card',
  wallet: 'wallet',
  dollarSign: 'dollar-sign',
  coin: 'coin',
  receipt: 'receipt',

  // 通讯/消息类
  phone: 'phone',
  mail: 'mail',
  message: 'message',
  messageCircle: 'message-circle',
  bell: 'bell',

  // 媒体类
  image: 'image',
  video: 'video',
  camera: 'camera',
  mic: 'mic',
  music: 'music',
  play: 'play',
  pause: 'pause',
  volume: 'volume',
  volume2: 'volume-2',
  volumeX: 'volume-x',

  // 地点/时间类
  mapPin: 'map-pin',
  map: 'map',
  clock: 'clock',
  calendar: 'calendar',
  timer: 'timer',

  // 文件/内容类
  file: 'file',
  fileText: 'file-text',
  folder: 'folder',
  folderOpen: 'folder-open',
  archive: 'archive',
  book: 'book',
  clipboard: 'clipboard',
  note: 'sticky-note',

  // 数据分析类
  barChart2: 'bar-chart-2',
  barChart3: 'bar-chart-3',
  barChart4: 'bar-chart-4',
  lineChart: 'line-chart',
  pieChart: 'pie-chart',
  trendingUp: 'trending-up',
  trendingDown: 'trending-down',
  areaChart: 'area-chart',

  // 状态/提示类
  alertCircle: 'alert-circle',
  alertTriangle: 'alert-triangle',
  alertOctagon: 'alert-octagon',
  helpCircle: 'help-circle',
  info: 'info',

  // 其他
  star: 'star',
  heart: 'heart',
  gift: 'gift',
  award: 'award',
  medal: 'medal',
  trophy: 'trophy',
  crown: 'crown',
  sparkles: 'sparkles',
  rocket: 'rocket',
  target: 'target',
  palette: 'palette',
  cloud: 'cloud',
  sun: 'sun',
  moon: 'moon',
  wifi: 'wifi',
  battery: 'battery',
  bluetooth: 'bluetooth',
  cpu: 'cpu',
  database: 'database',
  server: 'server',
  hardDrive: 'hard-drive',
  monitor: 'monitor',
  laptop: 'laptop',
  smartphone: 'smartphone',
  tablet: 'tablet',
  keyboard: 'keyboard',
  mouse: 'mouse',
  speaker: 'speaker',
  router: 'router',
  satellite: 'satellite',

  // 社交媒体
  facebook: 'facebook',
  twitter: 'twitter',
  github: 'github',
  gitlab: 'gitlab',
  linkedin: 'linkedin',
  instagram: 'instagram',
  youtube: 'youtube',
  dribbble: 'dribbble',
  figma: 'figma',
  slack: 'slack',
  trello: 'trello',
  twitch: 'twitch',
  chrome: 'chrome',
};

// ============ 完整图标列表 (按字母顺序) ============
// 可用于搜索和查找图标

export const ALL_ICONS = [
  // A
  'accessibility', 'activity-square', 'activity', 'air-vent', 'airplay',
  'alarm-clock-check', 'alarm-clock-off', 'alarm-clock', 'alarm-minus', 'alarm-plus',
  'album', 'alert-circle', 'alert-octagon', 'alert-triangle',
  'align-center-horizontal', 'align-center-vertical', 'align-center',
  'align-end-horizontal', 'align-end-vertical',
  'align-horizontal-distribute-center', 'align-horizontal-distribute-end', 'align-horizontal-distribute-start',
  'align-horizontal-justify-center', 'align-horizontal-justify-end', 'align-horizontal-justify-start',
  'align-horizontal-space-around', 'align-horizontal-space-between',
  'align-justify', 'align-left', 'align-right',
  'align-start-horizontal', 'align-start-vertical',
  'align-vertical-distribute-center', 'align-vertical-distribute-end', 'align-vertical-distribute-start',
  'align-vertical-justify-center', 'align-vertical-justify-end', 'align-vertical-justify-start',
  'align-vertical-space-around', 'align-vertical-space-between',
  'ampersand', 'ampersands', 'anchor', 'angry', 'annoyed', 'antenna',
  'aperture', 'app-window', 'apple', 'archive-restore', 'archive-x', 'archive', 'area-chart',
  'armchair', 'arrow-big-down-dash', 'arrow-big-down', 'arrow-big-left-dash', 'arrow-big-left',
  'arrow-big-right-dash', 'arrow-big-right', 'arrow-big-up-dash', 'arrow-big-up',
  'arrow-down-0-1', 'arrow-down-1-0', 'arrow-down-a-z', 'arrow-down-circle',
  'arrow-down-from-line', 'arrow-down-left-from-circle', 'arrow-down-left-square', 'arrow-down-left',
  'arrow-down-narrow-wide', 'arrow-down-right-from-circle', 'arrow-down-right-square', 'arrow-down-right',
  'arrow-down-square', 'arrow-down-to-dot', 'arrow-down-to-line', 'arrow-down-up', 'arrow-down-wide-narrow',
  'arrow-down-z-a', 'arrow-down',
  'arrow-left-circle', 'arrow-left-from-line', 'arrow-left-right', 'arrow-left-square', 'arrow-left-to-line', 'arrow-left',
  'arrow-right-circle', 'arrow-right-from-line', 'arrow-right-left', 'arrow-right-square', 'arrow-right-to-line', 'arrow-right',
  'arrow-up-0-1', 'arrow-up-1-0', 'arrow-up-a-z', 'arrow-up-circle',
  'arrow-up-down', 'arrow-up-from-dot', 'arrow-up-from-line',
  'arrow-up-left-from-circle', 'arrow-up-left-square', 'arrow-up-left',
  'arrow-up-narrow-wide', 'arrow-up-right-from-circle', 'arrow-up-right-square', 'arrow-up-right',
  'arrow-up-square', 'arrow-up-to-line', 'arrow-up-wide-narrow', 'arrow-up-z-a', 'arrow-up',
  'arrows-up-from-line', 'asterisk', 'at-sign', 'atom', 'audio-lines', 'audio-waveform',
  'award', 'axe', 'axis-3d',

  // B
  'baby', 'backpack',
  'badge-alert', 'badge-cent', 'badge-check', 'badge-dollar-sign', 'badge-euro',
  'badge-help', 'badge-indian-rupee', 'badge-info', 'badge-japanese-yen',
  'badge-minus', 'badge-percent', 'badge-plus', 'badge-pound-sterling',
  'badge-russian-ruble', 'badge-swiss-franc', 'badge-x', 'badge',
  'baggage-claim', 'ban', 'banana', 'banknote',
  'bar-chart-2', 'bar-chart-3', 'bar-chart-4', 'bar-chart-big', 'bar-chart-horizontal-big',
  'bar-chart-horizontal', 'bar-chart', 'barcode', 'baseline', 'bath',
  'battery-charging', 'battery-full', 'battery-low', 'battery-medium', 'battery-warning', 'battery',
  'beaker', 'bean-off', 'bean', 'bed-double', 'bed-single', 'bed', 'beef', 'beer',
  'bell-dot', 'bell-minus', 'bell-off', 'bell-plus', 'bell-ring', 'bell',
  'bike', 'binary', 'biohazard', 'bird', 'bitcoin',
  'blinds', 'blocks', 'bluetooth-connected', 'bluetooth-off', 'bluetooth-searching', 'bluetooth',
  'bold', 'bomb', 'bone',
  'book-a', 'book-audio', 'book-check', 'book-copy', 'book-dashed', 'book-down',
  'book-headphones', 'book-heart', 'book-image', 'book-key', 'book-lock',
  'book-marked', 'book-minus', 'book-open-check', 'book-open-text', 'book-open',
  'book-plus', 'book-text', 'book-type', 'book-up-2', 'book-up', 'book-user', 'book-x', 'book',
  'bookmark-check', 'bookmark-minus', 'bookmark-plus', 'bookmark-x', 'bookmark',
  'boom-box', 'bot', 'box-select', 'box', 'boxes', 'braces', 'brackets',
  'brain-circuit', 'brain-cog', 'brain', 'briefcase', 'bring-to-front', 'brush',
  'bug-off', 'bug-play', 'bug', 'building-2', 'building', 'bus-front', 'bus',

  // C
  'cable-car', 'cable', 'cake-slice', 'cake', 'calculator',
  'calendar-check-2', 'calendar-check', 'calendar-clock', 'calendar-days', 'calendar-heart',
  'calendar-minus', 'calendar-off', 'calendar-plus', 'calendar-range', 'calendar-search',
  'calendar-x-2', 'calendar-x', 'calendar',
  'camera-off', 'camera', 'candlestick-chart', 'candy-cane', 'candy-off', 'candy',
  'car-front', 'car-taxi-front', 'car', 'caravan', 'carrot',
  'case-lower', 'case-sensitive', 'case-upper', 'cassette-tape', 'cast', 'castle', 'cat',
  'check-check', 'check-circle-2', 'check-circle', 'check-square-2', 'check-square', 'check',
  'chef-hat', 'cherry',
  'chevron-down-circle', 'chevron-down-square', 'chevron-down',
  'chevron-first', 'chevron-last',
  'chevron-left-circle', 'chevron-left-square', 'chevron-left',
  'chevron-right-circle', 'chevron-right-square', 'chevron-right',
  'chevron-up-circle', 'chevron-up-square', 'chevron-up',
  'chevrons-down-up', 'chevrons-down', 'chevrons-left-right', 'chevrons-left',
  'chevrons-right-left', 'chevrons-right', 'chevrons-up-down', 'chevrons-up',
  'chrome', 'church', 'cigarette-off', 'cigarette',
  'circle-dashed', 'circle-dollar-sign', 'circle-dot-dashed', 'circle-dot',
  'circle-ellipsis', 'circle-equal', 'circle-off', 'circle-slash-2', 'circle-slash',
  'circle-user-round', 'circle-user', 'circle', 'circuit-board', 'citrus',
  'clapperboard',
  'clipboard-check', 'clipboard-copy', 'clipboard-edit', 'clipboard-list', 'clipboard-paste',
  'clipboard-signature', 'clipboard-type', 'clipboard-x', 'clipboard',
  'clock-1', 'clock-10', 'clock-11', 'clock-12', 'clock-2', 'clock-3', 'clock-4', 'clock-5',
  'clock-6', 'clock-7', 'clock-8', 'clock-9', 'clock',
  'cloud-cog', 'cloud-drizzle', 'cloud-fog', 'cloud-hail', 'cloud-lightning',
  'cloud-moon-rain', 'cloud-moon', 'cloud-off', 'cloud-rain-wind', 'cloud-rain',
  'cloud-snow', 'cloud-sun-rain', 'cloud-sun', 'cloud', 'cloudy',
  'clover', 'club', 'code-2', 'code', 'codepen', 'codesandbox', 'coffee',
  'cog', 'coins', 'columns', 'combine', 'command', 'compass', 'component',
  'computer', 'concierge-bell', 'cone', 'construction',
  'contact-2', 'contact', 'container', 'contrast', 'cookie',
  'copy-check', 'copy-minus', 'copy-plus', 'copy-slash', 'copy-x', 'copy',
  'copyleft', 'copyright',
  'corner-down-left', 'corner-down-right', 'corner-left-down', 'corner-left-up',
  'corner-right-down', 'corner-right-up', 'corner-up-left', 'corner-up-right',
  'cpu', 'creative-commons', 'credit-card', 'croissant', 'crop', 'cross', 'crosshair',
  'crown', 'cuboid', 'cup-soda', 'currency', 'cylinder',

  // D
  'database-backup', 'database-zap', 'database', 'delete', 'dessert', 'diameter', 'diamond',
  'dice-1', 'dice-2', 'dice-3', 'dice-4', 'dice-5', 'dice-6', 'dices',
  'diff', 'disc-2', 'disc-3', 'disc-album', 'disc',
  'divide-circle', 'divide-square', 'divide',
  'dna-off', 'dna', 'dog', 'dollar-sign', 'donut', 'door-closed', 'door-open', 'dot',
  'download-cloud', 'download', 'drafting-compass', 'drama', 'dribbble', 'droplet', 'droplets',
  'drum', 'drumstick', 'dumbbell',

  // E
  'ear-off', 'ear', 'egg-fried', 'egg-off', 'egg', 'equal-not', 'equal', 'eraser', 'euro',
  'expand', 'external-link', 'eye-off', 'eye',

  // F
  'facebook', 'factory', 'fan', 'fast-forward', 'feather', 'ferris-wheel', 'figma',
  'file-archive', 'file-audio-2', 'file-audio', 'file-axis-3d',
  'file-badge-2', 'file-badge', 'file-bar-chart-2', 'file-bar-chart', 'file-box',
  'file-check-2', 'file-check', 'file-clock', 'file-code-2', 'file-code', 'file-cog',
  'file-diff', 'file-digit', 'file-down', 'file-edit', 'file-heart', 'file-image', 'file-input',
  'file-json-2', 'file-json', 'file-key-2', 'file-key', 'file-line-chart',
  'file-lock-2', 'file-lock', 'file-minus-2', 'file-minus', 'file-music', 'file-output',
  'file-pie-chart', 'file-plus-2', 'file-plus', 'file-question', 'file-scan',
  'file-search-2', 'file-search', 'file-signature', 'file-spreadsheet', 'file-stack',
  'file-symlink', 'file-terminal', 'file-text', 'file-type-2', 'file-type',
  'file-up', 'file-video-2', 'file-video', 'file-volume-2', 'file-volume',
  'file-warning', 'file-x-2', 'file-x', 'file', 'files', 'film',
  'filter-x', 'filter', 'fingerprint', 'fish-off', 'fish-symbol', 'fish',
  'flag-off', 'flag-triangle-left', 'flag-triangle-right', 'flag',
  'flame-kindling', 'flame', 'flashlight-off', 'flashlight',
  'flask-conical-off', 'flask-conical', 'flask-round',
  'flip-horizontal-2', 'flip-horizontal', 'flip-vertical-2', 'flip-vertical',
  'flower-2', 'flower', 'focus', 'fold-horizontal', 'fold-vertical',
  'folder-archive', 'folder-check', 'folder-clock', 'folder-closed', 'folder-cog',
  'folder-dot', 'folder-down', 'folder-edit', 'folder-git-2', 'folder-git', 'folder-heart',
  'folder-input', 'folder-kanban', 'folder-key', 'folder-lock', 'folder-minus',
  'folder-open-dot', 'folder-open', 'folder-output', 'folder-plus', 'folder-root',
  'folder-search-2', 'folder-search', 'folder-symlink', 'folder-sync', 'folder-tree',
  'folder-up', 'folder-x', 'folder', 'folders', 'footprints', 'forklift', 'form-input',
  'forward', 'frame', 'framer', 'frown', 'fuel', 'fullscreen', 'function-square',

  // G
  'gallery-horizontal-end', 'gallery-horizontal', 'gallery-thumbnails', 'gallery-vertical-end', 'gallery-vertical',
  'gamepad-2', 'gamepad', 'gantt-chart-square', 'gantt-chart', 'gauge-circle', 'gauge',
  'gavel', 'gem', 'ghost', 'gift',
  'git-branch-plus', 'git-branch', 'git-commit-horizontal', 'git-commit-vertical',
  'git-compare-arrows', 'git-compare', 'git-fork', 'git-graph', 'git-merge',
  'git-pull-request-arrow', 'git-pull-request-closed', 'git-pull-request-create-arrow',
  'git-pull-request-create', 'git-pull-request-draft', 'git-pull-request', 'github', 'gitlab',
  'glass-water', 'glasses', 'globe-2', 'globe', 'goal', 'grab', 'graduation-cap', 'grape',
  'grid-2x2', 'grid-3x3', 'grip-horizontal', 'grip-vertical', 'grip', 'group', 'guitar',

  // H
  'hammer', 'hand-metal', 'hand',
  'hard-drive-download', 'hard-drive-upload', 'hard-drive', 'hard-hat', 'hash', 'haze',
  'hdmi-port',
  'heading-1', 'heading-2', 'heading-3', 'heading-4', 'heading-5', 'heading-6', 'heading',
  'headphones', 'heart-crack', 'heart-handshake', 'heart-off', 'heart-pulse', 'heart',
  'help-circle', 'helping-hand', 'hexagon', 'highlighter', 'history', 'home',
  'hop-off', 'hop', 'hotel', 'hourglass',

  // I
  'ice-cream-2', 'ice-cream',
  'image-down', 'image-minus', 'image-off', 'image-plus', 'image', 'import', 'inbox', 'indent',
  'indian-rupee', 'infinity', 'info', 'instagram', 'italic', 'iteration-ccw', 'iteration-cw',

  // J
  'japanese-yen', 'joystick',

  // K
  'kanban-square-dashed', 'kanban-square', 'kanban',
  'key-round', 'key-square', 'key', 'keyboard-music', 'keyboard',

  // L
  'lamp-ceiling', 'lamp-desk', 'lamp-floor', 'lamp-wall-down', 'lamp-wall-up', 'lamp',
  'land-plot', 'landmark', 'languages', 'laptop-2', 'laptop', 'lasso-select', 'lasso',
  'laugh', 'layers-2', 'layers-3', 'layers',
  'layout-dashboard', 'layout-grid', 'layout-list', 'layout-panel-left', 'layout-panel-top',
  'layout-template', 'layout', 'leaf', 'leafy-green',
  'library-big', 'library-square', 'library', 'life-buoy', 'ligature',
  'lightbulb-off', 'lightbulb', 'line-chart',
  'link-2-off', 'link-2', 'link', 'linkedin',
  'list-checks', 'list-end', 'list-filter', 'list-minus', 'list-music',
  'list-ordered', 'list-plus', 'list-restart', 'list-start', 'list-todo', 'list-tree',
  'list-video', 'list-x', 'list', 'loader-2', 'loader',
  'locate-fixed', 'locate-off', 'locate', 'lock-keyhole', 'lock', 'log-in', 'log-out',
  'lollipop', 'luggage',

  // M
  'm-square', 'magnet',
  'mail-check', 'mail-minus', 'mail-open', 'mail-plus', 'mail-question',
  'mail-search', 'mail-warning', 'mail-x', 'mail', 'mailbox', 'mails',
  'map-pin-off', 'map-pin', 'map-pinned', 'map', 'martini',
  'maximize-2', 'maximize', 'medal', 'megaphone-off', 'megaphone', 'meh',
  'memory-stick', 'menu-square', 'menu', 'merge',
  'message-circle', 'message-square-dashed', 'message-square-plus', 'message-square',
  'messages-square',
  'mic-2', 'mic-off', 'mic', 'microscope', 'microwave', 'milestone', 'milk-off', 'milk',
  'minimize-2', 'minimize', 'minus-circle', 'minus-square', 'minus',
  'monitor-check', 'monitor-dot', 'monitor-down', 'monitor-off', 'monitor-pause',
  'monitor-play', 'monitor-smartphone', 'monitor-speaker', 'monitor-stop', 'monitor-up',
  'monitor-x', 'monitor',
  'moon-star', 'moon', 'more-horizontal', 'more-vertical', 'mountain-snow', 'mountain',
  'mouse-pointer-2', 'mouse-pointer-click', 'mouse-pointer-square-dashed', 'mouse-pointer-square',
  'mouse-pointer', 'mouse',
  'move-3d', 'move-diagonal-2', 'move-diagonal', 'move-down-left', 'move-down-right', 'move-down',
  'move-horizontal', 'move-left', 'move-right', 'move-up-left', 'move-up-right', 'move-up',
  'move-vertical', 'move',
  'music-2', 'music-3', 'music-4', 'music',

  // N
  'navigation-2-off', 'navigation-2', 'navigation-off', 'navigation', 'network', 'newspaper', 'nfc',
  'nut-off', 'nut',

  // O
  'octagon', 'option', 'orbit', 'outdent',

  // P
  'package-2', 'package-check', 'package-minus', 'package-open', 'package-plus',
  'package-search', 'package-x', 'package',
  'paint-bucket', 'paintbrush-2', 'paintbrush', 'palette', 'palmtree',
  'panel-bottom-close', 'panel-bottom-inactive', 'panel-bottom-open', 'panel-bottom',
  'panel-left-close', 'panel-left-inactive', 'panel-left-open', 'panel-left',
  'panel-right-close', 'panel-right-inactive', 'panel-right-open', 'panel-right',
  'panel-top-close', 'panel-top-inactive', 'panel-top-open', 'panel-top',
  'paperclip', 'parentheses',
  'parking-circle-off', 'parking-circle', 'parking-meter', 'parking-square-off', 'parking-square',
  'party-popper', 'pause-circle', 'pause-octagon', 'pause', 'paw-print', 'pc-case',
  'pen-line', 'pen-square', 'pen-tool', 'pen', 'pencil-line', 'pencil-ruler', 'pencil',
  'pentagon', 'percent-circle', 'percent-diamond', 'percent-square', 'percent', 'person-standing',
  'phone-call', 'phone-forwarded', 'phone-incoming', 'phone-missed', 'phone-off',
  'phone-outgoing', 'phone', 'pi-square', 'pi', 'piano',
  'picture-in-picture-2', 'picture-in-picture', 'pie-chart', 'piggy-bank',
  'pilcrow-square', 'pilcrow', 'pill', 'pin-off', 'pin', 'pipette', 'pizza',
  'plane-landing', 'plane-takeoff', 'plane',
  'play-circle', 'play-square', 'play',
  'plug-2', 'plug-zap-2', 'plug-zap', 'plug', 'plus-circle', 'plus-square', 'plus',
  'pocket-knife', 'pocket', 'podcast', 'pointer', 'popcorn', 'popsicle',
  'pound-sterling', 'power-circle', 'power-off', 'power-square', 'power',
  'presentation', 'printer', 'projector', 'puzzle', 'pyramid',

  // Q
  'qr-code', 'quote',

  // R
  'rabbit', 'radar', 'radiation', 'radio-receiver', 'radio-tower', 'radio', 'radius',
  'rail-symbol', 'rainbow', 'rat', 'ratio', 'receipt',
  'rectangle-horizontal', 'rectangle-vertical', 'recycle', 'redo-2', 'redo-dot', 'redo',
  'refresh-ccw-dot', 'refresh-ccw', 'refresh-cw-off', 'refresh-cw', 'refrigerator', 'regex',
  'remove-formatting', 'repeat-1', 'repeat-2', 'repeat', 'replace-all', 'replace',
  'reply-all', 'reply', 'rewind', 'ribbon', 'rocket', 'rocking-chair', 'roller-coaster',
  'rotate-3d', 'rotate-ccw', 'rotate-cw', 'route-off', 'route', 'router', 'rows', 'rss', 'ruler', 'russian-ruble',

  // S
  'sailboat', 'salad', 'sandwich', 'satellite-dish', 'satellite',
  'save-all', 'save', 'scale-3d', 'scale', 'scaling',
  'scan-barcode', 'scan-eye', 'scan-face', 'scan-line', 'scan-search', 'scan-text', 'scan',
  'scatter-chart', 'school-2', 'school',
  'scissors-line-dashed', 'scissors-square-dashed-bottom', 'scissors-square', 'scissors',
  'screen-share-off', 'screen-share', 'scroll-text', 'scroll',
  'search-check', 'search-code', 'search-slash', 'search-x', 'search',
  'send-horizontal', 'send-to-back', 'send',
  'separator-horizontal', 'separator-vertical',
  'server-cog', 'server-crash', 'server-off', 'server',
  'settings-2', 'settings', 'shapes', 'share-2', 'share', 'sheet', 'shell',
  'shield-alert', 'shield-ban', 'shield-check', 'shield-ellipsis', 'shield-half',
  'shield-minus', 'shield-off', 'shield-plus', 'shield-question', 'shield-x', 'shield',
  'ship-wheel', 'ship', 'shirt', 'shopping-bag', 'shopping-basket', 'shopping-cart',
  'shovel', 'shower-head', 'shrink', 'shrub', 'shuffle',
  'sigma-square', 'sigma',
  'signal-high', 'signal-low', 'signal-medium', 'signal-zero', 'signal',
  'signpost-big', 'signpost', 'siren',
  'skip-back', 'skip-forward', 'skull', 'slack', 'slash', 'slice',
  'sliders-horizontal', 'sliders',
  'smartphone-charging', 'smartphone-nfc', 'smartphone',
  'smile-plus', 'smile', 'snail', 'snowflake', 'sofa', 'soup', 'space', 'spade',
  'sparkle', 'sparkles', 'speaker', 'speech', 'spell-check-2', 'spell-check',
  'spline', 'split-square-horizontal', 'split-square-vertical', 'split', 'spray-can', 'sprout',
  'square-asterisk', 'square-code', 'square-dashed-bottom-code', 'square-dashed-bottom',
  'square-dot', 'square-equal', 'square-slash', 'square-stack', 'square-user-round', 'square-user', 'square',
  'squirrel', 'stamp', 'star-half', 'star-off', 'star',
  'step-back', 'step-forward', 'stethoscope', 'sticker', 'sticky-note',
  'stop-circle', 'store', 'stretch-horizontal', 'stretch-vertical', 'strikethrough',
  'subscript', 'subtitles',
  'sun-dim', 'sun-medium', 'sun-moon', 'sun-snow', 'sun', 'sunrise', 'sunset', 'superscript',
  'swiss-franc', 'switch-camera', 'sword', 'swords', 'syringe',

  // T
  'table-2', 'table-properties', 'table', 'tablet-smartphone', 'tablet', 'tablets',
  'tag', 'tags', 'tally-1', 'tally-2', 'tally-3', 'tally-4', 'tally-5',
  'tangent', 'target', 'tent-tree', 'tent', 'terminal-square', 'terminal',
  'test-tube-2', 'test-tube', 'test-tubes',
  'text-cursor-input', 'text-cursor', 'text-quote', 'text-select', 'text',
  'theater', 'thermometer-snowflake', 'thermometer-sun', 'thermometer',
  'thumbs-down', 'thumbs-up', 'ticket', 'timer-off', 'timer-reset', 'timer',
  'toggle-left', 'toggle-right', 'tornado', 'torus', 'touchpad-off', 'touchpad',
  'tower-control', 'toy-brick', 'tractor', 'traffic-cone',
  'train-front-tunnel', 'train-front', 'train-track', 'tram-front',
  'trash-2', 'trash', 'tree-deciduous', 'tree-pine', 'trees', 'trello',
  'trending-down', 'trending-up', 'triangle-right', 'triangle', 'trophy', 'truck', 'turtle',
  'tv-2', 'tv', 'twitch', 'twitter', 'type',

  // U
  'umbrella-off', 'umbrella', 'underline', 'undo-2', 'undo-dot', 'undo',
  'unfold-horizontal', 'unfold-vertical', 'ungroup', 'unlink-2', 'unlink',
  'unlock-keyhole', 'unlock', 'unplug', 'upload-cloud', 'upload', 'usb',
  'user-check', 'user-cog', 'user-minus', 'user-plus', 'user-round-check', 'user-round-cog',
  'user-round-minus', 'user-round-plus', 'user-round-x', 'user-round', 'user-x', 'user',
  'users-round', 'users', 'utensils-crossed', 'utensils', 'utility-pole',

  // V
  'variable', 'vegan', 'venetian-mask', 'vibrate-off', 'vibrate', 'video-off', 'video',
  'videotape', 'view', 'voicemail', 'volume-1', 'volume-2', 'volume-x', 'volume', 'vote',

  // W
  'wallet-2', 'wallet-cards', 'wallet', 'wallpaper', 'wand-2', 'wand', 'warehouse',
  'watch', 'waves', 'waypoints', 'webcam', 'webhook', 'weight',
  'wheat-off', 'wheat', 'whole-word', 'wifi-off', 'wifi', 'wind', 'wine-off', 'wine',
  'workflow', 'wrap-text', 'wrench',

  // X
  'x-circle', 'x-octagon', 'x-square', 'x',

  // Y
  'youtube',

  // Z
  'zap-off', 'zap', 'zoom-in', 'zoom-out',
];

// ============ 图标分类索引 ============

export const ICON_CATEGORIES = {
  navigation: {
    name: '导航类',
    icons: ['menu', 'x', 'chevron-left', 'chevron-right', 'chevron-down', 'chevron-up',
      'arrow-left', 'arrow-right', 'arrow-up', 'arrow-down', 'home', 'layout', 'grid', 'list',
      'more-horizontal', 'more-vertical', 'panel-left', 'panel-right', 'panel-bottom', 'panel-top',
      'sidebar', 'columns', 'rows'],
  },
  action: {
    name: '操作类',
    icons: ['plus', 'minus', 'check', 'check-circle', 'circle', 'trash', 'trash-2', 'delete',
      'edit', 'pencil', 'pen', 'copy', 'save', 'download', 'upload', 'search', 'filter',
      'refresh', 'undo', 'redo', 'send', 'share', 'share-2', 'bookmark', 'flag',
      'printer', 'link', 'unlink', 'external-link', 'crop', 'move', 'maximize', 'minimize',
      'fullscreen', 'shrink', 'expand', 'rotate-cw', 'rotate-ccw', 'repeat', 'shuffle'],
  },
  security: {
    name: '安全/权限类',
    icons: ['lock', 'unlock', 'lock-keyhole', 'unlock-keyhole', 'shield', 'shield-check',
      'shield-alert', 'shield-ban', 'shield-off', 'shield-ellipsis', 'shield-half',
      'shield-minus', 'shield-plus', 'shield-question', 'shield-x',
      'key', 'key-round', 'key-square', 'fingerprint', 'eye', 'eye-off'],
  },
  settings: {
    name: '设置类',
    icons: ['settings', 'settings-2', 'tool', 'wrench', 'sliders', 'sliders-horizontal',
      'toggle-left', 'toggle-right', 'power', 'power-off', 'log-in', 'log-out'],
  },
  user: {
    name: '用户/社交类',
    icons: ['user', 'user-plus', 'user-minus', 'user-check', 'user-x', 'user-cog',
      'user-round', 'user-round-check', 'user-round-plus', 'user-round-minus', 'user-round-x',
      'users', 'users-round', 'group', 'idCard', 'badge'],
  },
  business: {
    name: '企业/业务类',
    icons: ['building', 'briefcase', 'store', 'cart', 'shopping-cart', 'shopping-bag',
      'shopping-basket', 'credit-card', 'card', 'wallet', 'dollar-sign', 'euro', 'pound',
      'coin', 'money', 'piggy-bank', 'receipt', 'warehouse', 'factory', 'office', 'landmark'],
  },
  communication: {
    name: '通讯/消息类',
    icons: ['phone', 'phone-call', 'phone-incoming', 'phone-outgoing', 'phone-missed',
      'phone-forwarded', 'phone-off', 'mail', 'mail-check', 'mail-minus', 'mail-open',
      'mail-plus', 'mail-question', 'mail-search', 'mail-warning', 'mail-x', 'mailbox',
      'mails', 'message', 'message-circle', 'message-square', 'messages-square',
      'bell', 'bell-ring', 'bell-dot', 'bell-plus', 'bell-minus', 'bell-off'],
  },
  media: {
    name: '媒体类',
    icons: ['image', 'image-plus', 'image-minus', 'image-down', 'image-off', 'gallery-horizontal',
      'gallery-horizontal-end', 'gallery-vertical', 'gallery-vertical-end', 'gallery-thumbnails',
      'video', 'video-off', 'videotape', 'camera', 'camera-off', 'webcam',
      'mic', 'mic-off', 'mic-2', 'music', 'music-2', 'music-3', 'music-4',
      'play', 'play-circle', 'play-square', 'pause', 'pause-circle', 'pause-octagon',
      'stop-circle', 'volume', 'volume-1', 'volume-2', 'volume-x', 'speaker', 'headphones'],
  },
  location: {
    name: '地点/时间类',
    icons: ['map-pin', 'map-pin-off', 'map-pinned', 'map', 'navigation', 'navigation-off',
      'navigation-2', 'navigation-2-off', 'compass', 'locate', 'locate-fixed', 'locate-off',
      'crosshair', 'target', 'clock', 'timer', 'calendar', 'calendar-check', 'calendar-clock',
      'calendar-days', 'calendar-minus', 'calendar-off', 'calendar-plus', 'calendar-range',
      'calendar-search', 'calendar-x', 'calendar-heart', 'alarm-clock', 'alarm-clock-check',
      'alarm-clock-off', 'alarm-minus', 'alarm-plus'],
  },
  file: {
    name: '文件/内容类',
    icons: ['file', 'file-text', 'file-check', 'file-x', 'file-clock', 'file-edit',
      'file-search', 'file-question', 'file-warning', 'file-copy', 'file-down', 'file-up',
      'file-plus', 'file-minus', 'file-stack', 'file-box', 'file-archive', 'file-code',
      'file-audio', 'file-video', 'file-image', 'file-pie-chart', 'file-bar-chart',
      'file-line-chart', 'file-spreadsheet', 'file-signature', 'file-scan',
      'folder', 'folder-open', 'folder-check', 'folder-plus', 'folder-minus', 'folder-x',
      'folder-closed', 'folder-dot', 'folder-clock', 'folder-edit', 'folder-search',
      'folder-heart', 'folder-key', 'folder-lock', 'folder-cog', 'folder-input', 'folder-output',
      'folder-sync', 'folder-tree', 'folder-archive', 'folder-root', 'folder-kanban',
      'folder-git', 'folders', 'archive', 'archive-restore', 'archive-x',
      'book', 'book-open', 'bookmark', 'bookmark-plus', 'bookmark-minus', 'bookmark-check',
      'clipboard', 'clipboard-check', 'clipboard-copy', 'clipboard-edit', 'clipboard-list',
      'clipboard-paste', 'clipboard-x', 'clipboard-type', 'clipboard-signature',
      'sticky-note', 'scroll', 'newspaper'],
  },
  chart: {
    name: '数据分析类',
    icons: ['bar-chart', 'bar-chart-2', 'bar-chart-3', 'bar-chart-4', 'bar-chart-big',
      'bar-chart-horizontal', 'bar-chart-horizontal-big', 'line-chart', 'pie-chart',
      'area-chart', 'scatter-chart', 'trending-up', 'trending-down', 'activity',
      'radar', 'gauge', 'gauge-circle', 'candlestick-chart',
      'file-pie-chart', 'file-bar-chart', 'file-line-chart'],
  },
  status: {
    name: '状态/提示类',
    icons: ['alert-circle', 'alert-triangle', 'alert-octagon', 'alert',
      'help-circle', 'info', 'check-circle', 'x-circle', 'x-octagon', 'x-square',
      'plus-circle', 'minus-circle', 'pause-circle', 'stop-circle',
      'loader', 'loader-2', 'refresh-ccw', 'refresh-cw'],
  },
  social: {
    name: '社交媒体类',
    icons: ['facebook', 'twitter', 'github', 'gitlab', 'linkedin', 'instagram',
      'youtube', 'dribbble', 'figma', 'slack', 'trello', 'twitch', 'chrome'],
  },
  common: {
    name: '通用/其他',
    icons: ['star', 'star-half', 'star-off', 'heart', 'heart-crack', 'heart-off', 'heart-pulse',
      'heart-handshake', 'gift', 'award', 'medal', 'trophy', 'crown', 'sparkles', 'sparkle',
      'rocket', 'palette', 'cloud', 'cloud-off', 'cloud-drizzle', 'cloud-fog', 'cloud-hail',
      'cloud-lightning', 'cloud-rain', 'cloud-snow', 'cloud-sun', 'cloud-moon',
      'sun', 'sun-dim', 'sun-medium', 'sun-moon', 'sun-snow', 'sunrise', 'sunset',
      'moon', 'moon-star', 'wifi', 'wifi-off', 'bluetooth', 'bluetooth-connected',
      'bluetooth-searching', 'bluetooth-off', 'battery', 'battery-full', 'battery-low',
      'battery-medium', 'battery-charging', 'battery-warning', 'cpu', 'database',
      'server', 'server-cog', 'server-crash', 'server-off', 'hard-drive',
      'hard-drive-download', 'hard-drive-upload', 'monitor', 'monitor-off', 'monitor-play',
      'monitor-pause', 'monitor-stop', 'monitor-check', 'monitor-x', 'monitor-dot',
      'monitor-up', 'monitor-down', 'monitor-smartphone', 'monitor-speaker',
      'laptop', 'tablet', 'smartphone', 'smartphone-charging', 'smartphone-nfc',
      'keyboard', 'mouse', 'speaker', 'printer', 'router', 'satellite',
      'satellite-dish', 'usb', 'gamepad', 'gamepad-2', 'target', 'crosshair',
      'binoculars', 'telescope', 'microscope', 'beaker', 'flask-conical',
      'flask-conical-off', 'flask-round', 'test-tube', 'test-tube-2', 'test-tubes',
      'pill', 'syringe', 'stethoscope', 'thermometer', 'thermometer-sun',
      'thermometer-snowflake', 'dna', 'dna-off', 'scan', 'scan-face',
      'scan-eye', 'scan-barcode', 'scan-line', 'scan-search', 'scan-text'],
  },
};

// ============ 使用示例 ============
/**
 * 1. 静态导入并使用:
 *    import { Search, Plus, Edit } from 'lucide-react';
 *    <Search className="w-5 h-5" />
 * 
 * 2. 动态导入 (代码分割):
 *    import { dynamicIconImports } from 'lucide-react';
 *    const Search = dynamicIconImports['search'];
 *    <Search className="w-5 h-5" />
 * 
 * 3. 从本文件使用图标名称:
 *    import { CommonIconsList } from '@/components/lucideIcons';
 *    import { Search } from 'lucide-react';
 *    <Search className="w-5 h-5" />
 * 
 * 4. 图标规格:
 *    - 所有图标都是 24x24 的 viewBox
 *    - 使用 stroke 渲染，可通过 className 调整颜色和大小
 *    - 支持 strokeWidth, strokeLinecap, strokeLinejoin 等属性
 */

// ============ Emoji 图标列表 ============
// 丰富的 emoji 图标集合，可用于替代 lucide-react 图标

export const EMOJI_ICONS = {
  // 建筑/企业类
  building: '🏢',
  office: '🏬',
  factory: '🏭',
  construction: '🏗️',
  house: '🏠',
  villa: '🏡',
  library: '📚',
  hotel: '🏨',
  hospital: '🏥',
  school: '🏫',
  university: '🎓',
  bank: '🏦',
  shop: '🏪',
  store: '🛍️',
  
  // 业务/工作类
  briefcase: '💼',
  businessman: '👔',
  handshake: '🤝',
  meeting: '👥',
  presentation: '📊',
  contract: '📝',
  money: '💰',
  moneybag: '👛',
  creditcard: '💳',
  coin: '🪙',
  dollar: '💵',
  euro: '💶',
  pound: '💷',
  yen: '💴',
  check: '✅',
  checklist: '📋',
  
  // 通讯/社交类
  phone: '📱',
  telephone: '☎️',
  email: '📧',
  mail: '📨',
  message: '💬',
  sms: '💭',
  bell: '🔔',
  notification: '🔔',
  megaphone: '📢',
  loudspeaker: '📣',
  radio: '📻',
  television: '📺',
  camera: '📷',
  video: '🎥',
  
  // 用户/人员类
  user: '👤',
  users: '👥',
  businessman2: '👨‍💼',
  businesswoman: '👩‍💼',
  developer: '👨‍💻',
  engineer: '👷',
  scientist: '👨‍🔬',
  teacher: '👨‍🏫',
  doctor: '👨‍⚕️',
  chef: '👨‍🍳',
  artist: '👨‍🎨',
  musician: '🎤',
  singer: '🎙️',
  
  // 科技/互联网类
  computer: '💻',
  laptop: '💻',
  desktop: '🖥️',
  keyboard: '⌨️',
  mouse: '🖱️',
  printer: '🖨️',
  scanner: '📷',
  wifi: '📶',
  signal: '📡',
  satellite: '🛰️',
  rocket: '🚀',
  spaceship: '🛸',
  robot: '🤖',
  cpu: '🧠',
  battery: '🔋',
  electricity: '⚡',
  plug: '🔌',
  
  // 媒体/内容类
  image: '🖼️',
  gallery: '🖼️',
  photo: '📸',
  picture: '🖼️',
  document: '📄',
  file: '📄',
  folder: '📁',
  archive: '🗃️',
  clipboard: '📋',
  notebook: '📓',
  notepad: '📝',
  book: '📖',
  bookmark: '🔖',
  pages: '📑',
  newspaper: '📰',
  magazine: '📖',
  
  // 音乐/艺术类
  music: '🎵',
  musicalnote: '🎶',
  piano: '🎹',
  guitar: '🎸',
  violin: '🎻',
  drum: '🥁',
  headphones: '🎧',
  speaker: '🔊',
  microphone: '🎤',
  theater: '🎭',
  cinema: '🎬',
  clapperboard: '🎬',
  palette: '🎨',
  paintbrush: '🖌️',
  crayon: '🖍️',
  
  // 交通工具类
  car: '🚗',
  truck: '🚚',
  bus: '🚌',
  train: '🚆',
  bullettrain: '🚅',
  airplane: '✈️',
  jet: '🛩️',
  helicopter: '🚁',
  boat: '🚤',
  ship: '🚢',
  bicycle: '🚲',
  motorcycle: '🏍️',
  
  // 自然/环境类
  sun: '☀️',
  moon: '🌙',
  cloud: '☁️',
  rainbow: '🌈',
  storm: '⛈️',
  snow: '❄️',
  snowflake: '❄️',
  wind: '🌬️',
  rain: '🌧️',
  water: '💧',
  fire: '🔥',
  tree: '🌳',
  leaf: '🍃',
  flower: '🌸',
  rose: '🌹',
  cactus: '🌵',
  earth: '🌍',
  globe: '🌐',
  
  // 食物/饮品类
  coffee: '☕',
  tea: '🍵',
  juice: '🧃',
  soda: '🥤',
  wine: '🍷',
  beer: '🍺',
  cake: '🎂',
  donut: '🍩',
  cookie: '🍪',
  pizza: '🍕',
  hamburger: '🍔',
  fries: '🍟',
  sushi: '🍣',
  rice: '🍚',
  noodle: '🍜',
  bread: '🍞',
  
  // 运动/娱乐类
  football: '⚽',
  basketball: '🏀',
  baseball: '⚾',
  tennis: '🎾',
  golf: '⛳',
  swimming: '🏊',
  running: '🏃',
  cycling: '🚴',
  skiing: '⛷️',
  snowboard: '🏂',
  trophy: '🏆',
  medal: '🥇',
  goldmedal: '🥇',
  gamepad: '🎮',
  dice: '🎲',
  playingcard: '🎴',
  puzzle: '🧩',
  
  // 状态/提示类
  checkmark: '✅',
  crossmark: '❌',
  warning: '⚠️',
  alert: '🚨',
  exclamation: '❗',
  question: '❓',
  information: 'ℹ️',
  idea: '💡',
  bulb: '💡',
  star: '⭐',
  star2: '🌟',
  sparkle: '✨',
  boom: '💥',
  explosion: '💣',
  
  // 符号/标记类
  heart: '❤️',
  heartbroken: '💔',
  thumbsup: '👍',
  thumbsdown: '👎',
  clap: '👏',
  wave: '👋',
  pointup: '👆',
  pointdown: '👇',
  pointleft: '👈',
  pointright: '👉',
  ok: '👌',
  victory: '✌️',
  peace: '☮️',
  yin_yang: '☯️',
  recycle: '♻️',
  
  // 工具/设备类
  hammer: '🔨',
  wrench: '🔧',
  screwdriver: '🪛',
  gear: '⚙️',
  gear2: '⚙️',
  link: '🔗',
  chain: '🔗',
  scissors: '✂️',
  lock: '🔒',
  unlock: '🔓',
  key: '🔑',
  shield: '🛡️',
  target: '🎯',
  compass: '🧭',
  telescope: '🔭',
  microscope: '🔬',
  
  // 财富/金融类
  gem: '💎',
  diamond: '💎',
  crown: '👑',
  gift: '🎁',
  present: '🎁',
  wallet: '👛',
  purse: '👜',
  shoppingbag: '🛍️',
  shoppingcart: '🛒',
  credit: '💳',
  atm: '🏧',
  piggybank: '🐷',
  
  // 地点/时间类
  location: '📍',
  pin: '📌',
  map: '🗺️',
  compass2: '🧭',
  clock: '🕐',
  watch: '⌚',
  hourglass: '⏳',
  hourglass2: '⌛',
  calendar: '📅',
  calendar2: '📆',
  pushpin: '📌',
  
  // 动物/生物类
  dog: '🐕',
  cat: '🐈',
  bird: '🐦',
  fish: '🐟',
  dolphin: '🐬',
  whale: '🐳',
  horse: '🐎',
  cow: '🐄',
  pig: '🐖',
  lion: '🦁',
  tiger: '🐯',
  elephant: '🐘',
  monkey: '🐵',
  panda: '🐼',
  frog: '🐸',
  
  // 表情/情绪类
  smile: '😊',
  laugh: '😂',
  grin: '😁',
  happy: '😄',
  wink: '😉',
  love: '😍',
  kiss: '😘',
  cool: '😎',
  think: '🤔',
  confused: '😕',
  sad: '😢',
  cry: '😭',
  angry: '😠',
  sleep: '😴',
  sick: '🤒',
  dizzy: '😵',
  woozy: '😷',
  
  // 其他常用
  rocketship: '🚀',
  helicopter2: '🚁',
  sailboat: '⛵',
  motorboat: '🚤',
  train2: '🚄',
  station: '🚉',
  fuel: '⛽',
  parking: '🅿️',
  construction2: '🚧',
  police: '🚓',
  ambulance: '🚑',
  fireengine: '🚒',
  hospital2: '🏥',
  pharmacy: '💊',
  medicine: '💉',
  
  // 科技/未来类
  atom: '⚛️',
  molecule: '🧬',
  dna: '🧬',
  energy: '⚡',
  power: '🔌',
  server: '🖥️',
  database: '🗄️',
  cloud2: '☁️',
  online: '💻',
  offline: '📴',
  sync: '🔄',
  update: '🔄',
  download: '⬇️',
  upload: '⬆️',
  refresh: '🔄',
  sync2: '♾️',
  
  // 商业/办公类
  meetingroom: '🏢',
  conference: '📊',
  brainstorm: '💡',
  strategy: '♟️',
  chess: '♟️',
  chess2: '♚',
  lightbulb: '💡',
  lamp: '💡',
  flashlight: '🔦',
  candle: '🕯️',
  fireworks: '🎆',
  sparkler: '🎇',
  confetti: '🎉',
  party: '🎊',
  celebration: '🎉',
  
  // 节日/庆祝类
  christmas: '🎄',
  snowman: '⛄',
  santa: '🎅',
  gift2: '🎁',
  wrapped: '🎀',
  balloon: '🎈',
  cake2: '🎂',
  birthday: '🎂',
  newyear: '🎊',
  celebration2: '🎉',
  
  // 旅行/出行类
  airplane2: '🛫',
  arrival: '🛬',
  departure: '🛫',
  passport: '🛂',
  luggage: '🧳',
  suitcase: '💼',
  hotel2: '🏨',
  motel: '🏨',
  cabin: '🛖',
  tent: '⛺',
  campfire: '🔥',
  bonfire: '🔥',
  picnic: '🧺',
  
  // 健康/医疗类
  heart2: '❤️',
  heartbeat: '💓',
  pulserate: '💗',
  blood: '🩸',
  medical: '⚕️',
  nurse: '👩‍⚕️',
  physician: '👨‍⚕️',
  stethoscope: '🩺',
  pill: '💊',
  pill2: '💊',
  capsule: '💊',
  tablet: '💊',
  thermometer: '🌡️',
  crutch: '🦯',
  wheelchair: '♿',
  
  // 其他
  palette2: '🎨',
  art: '🎨',
  design: '✏️',
  pencil: '✏️',
  pen: '🖊️',
  marker: '🖍️',
  highlighter: '🖊️',
  ruler: '📏',
  measuringtape: '📏',
  scissors2: '✂️',
  stapler: '📎',
  paperclip: '📎',
  tape: '🧻',
};

// Emoji 图标中文标签
export const emojiLabels: Record<string, string> = {
  building: '办公楼',
  office: '办公室',
  factory: '工厂',
  construction: '施工',
  house: '住宅',
  villa: '别墅',
  library: '图书馆',
  hotel: '酒店',
  hospital: '医院',
  school: '学校',
  university: '大学',
  bank: '银行',
  shop: '商店',
  store: '商场',
  briefcase: '公文包',
  businessman: '商务人士',
  handshake: '握手',
  meeting: '会议',
  presentation: '演示',
  contract: '合同',
  money: '金钱',
  moneybag: '钱包',
  creditcard: '信用卡',
  coin: '硬币',
  dollar: '美元',
  check: '勾选',
  checklist: '清单',
  phone: '手机',
  telephone: '电话',
  email: '邮件',
  mail: '信件',
  message: '消息',
  bell: '铃铛',
  megaphone: '扩音器',
  computer: '电脑',
  laptop: '笔记本',
  wifi: '无线网络',
  rocket: '火箭',
  robot: '机器人',
  cpu: '大脑',
  battery: '电池',
  image: '图片',
  document: '文档',
  file: '文件',
  folder: '文件夹',
  archive: '档案',
  book: '书籍',
  music: '音乐',
  camera: '相机',
  video: '视频',
  user: '用户',
  users: '用户组',
  car: '汽车',
  plane: '飞机',
  train: '火车',
  ship: '轮船',
  sun: '太阳',
  moon: '月亮',
  cloud: '云',
  fire: '火焰',
  tree: '树木',
  flower: '花朵',
  coffee: '咖啡',
  tea: '茶',
  cake: '蛋糕',
  football: '足球',
  basketball: '篮球',
  trophy: '奖杯',
  medal: '奖牌',
  checkmark: '对号',
  crossmark: '叉号',
  warning: '警告',
  heart: '爱心',
  star: '星星',
  idea: '想法',
  hammer: '锤子',
  wrench: '扳手',
  gear: '齿轮',
  lock: '锁',
  key: '钥匙',
  target: '目标',
  gift: '礼物',
  crown: '皇冠',
  gem: '宝石',
  location: '位置',
  clock: '时钟',
  calendar: '日历',
  dog: '狗',
  cat: '猫',
  bird: '鸟',
  smile: '微笑',
  happy: '开心',
  sad: '伤心',
  rocketship: '飞船',
  server: '服务器',
  database: '数据库',
  cloud2: '云存储',
  download: '下载',
  upload: '上传',
  refresh: '刷新',
  meetingroom: '会议室',
  strategy: '策略',
  lightbulb: '灯泡',
  christmas: '圣诞树',
  birthday: '生日',
  airplane2: '飞机起降',
  hotel2: '酒店',
  medical: '医疗',
  palette2: '调色板',
  design: '设计',
  pencil: '铅笔',
};

// 导出所有 emoji 图标的值数组
export const ALL_EMOJI_ICONS = Object.values(EMOJI_ICONS);